import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import useGetLoadBalancingGroup from 'hooks/useGetLoadBalancingGroup';
import { useTranslation } from 'next-i18next';
import type { FC, MouseEvent } from 'react';
import { useMemo } from 'react';
import type {
  GroupLoadBalancingData,
  LoadBalancingGroup,
} from 'utils/common/loadBalancingHelpers';
import { formatLoadBalancingData } from 'utils/common/loadBalancingHelpers';

import CustomListItem from 'components/DeviceConfigComponents/CustomListItem';

import { useAppSelector } from 'redux-store';

interface ExistingLoadBalancingGroupsProps {
  selectedGroupId?: string;
  onSelectGroup: (values: GroupLoadBalancingData) => void;
  onCreateGroup: () => void;
  onEditGroup: (values: GroupLoadBalancingData) => void;
}

const ExistingLoadBalancingGroups: FC<ExistingLoadBalancingGroupsProps> = ({
  selectedGroupId,
  onSelectGroup,
  onCreateGroup,
  onEditGroup,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const devices = useAppSelector(state => state.devices.items);
  const serials = useMemo(() => devices.map(d => d.serial), [devices]);
  const loadBalancingGroups = useGetLoadBalancingGroup(serials);

  const handleEditGroup = (e: MouseEvent, group: LoadBalancingGroup): void => {
    e.stopPropagation();
    onEditGroup(formatLoadBalancingData(group.data));
  };

  return (
    <Box>
      <Box mb={3}>
        <CustomListItem
          onClick={onCreateGroup}
          leftComponent={
            <Typography color="primary" fontWeight={600}>
              {t('common:pages.device_setup.load_balancing_setup.create_group')}
            </Typography>
          }
          rightComponent={
            <IconButton edge="start" disableRipple>
              <AddCircleOutlineRoundedIcon color="primary" />
            </IconButton>
          }
          isSingleItem
        />
      </Box>

      {loadBalancingGroups.length > 0 ? (
        <>
          <Typography variant="body1" fontWeight={600} mb={2} pl={1}>
            {t(
              'common:pages.settings.subpages.load_balancing.join_or_create_group.load_balancing_groups',
            )}
          </Typography>
          <Box>
            {loadBalancingGroups.map((group, index) => {
              const groupId = group.data.loadBalancingGroupId;
              const isFirst = index === 0;
              const isLast = index === loadBalancingGroups.length - 1;
              const isSelected = groupId === selectedGroupId;

              return (
                <CustomListItem
                  onClick={() =>
                    onSelectGroup(formatLoadBalancingData(group.data))
                  }
                  key={groupId}
                  leftComponent={
                    <IconButton disableRipple>
                      <CheckIcon
                        sx={{
                          color: isSelected
                            ? theme.vars.palette.primary.main
                            : 'transparent',
                        }}
                      />
                    </IconButton>
                  }
                  leftText={groupId}
                  isFirstItem={isFirst}
                  isLastItem={isLast}
                  rightComponent={
                    <IconButton
                      component="span"
                      size="small"
                      aria-label="edit group"
                      onClick={e => handleEditGroup(e, group)}
                    >
                      <EditOutlinedIcon sx={{ color: 'grey.500' }} />
                    </IconButton>
                  }
                />
              );
            })}
          </Box>
        </>
      ) : null}
    </Box>
  );
};

export default ExistingLoadBalancingGroups;
